/**
 * TopDown — Sector → Industry → Symbols in one screener panel
 * (`docs/features/topdown.md`). The pure half: the drill, the ticks, the
 * layout, and the index a tick charts. No React, no fetch.
 *
 * ```
 * base screen's rows ─► its filter ─► (members) ─┬─► sector ticks ─► industry ticks ─► Symbols grid
 *                                                └─► POST /sym/topdown/levels ─► Sector / Industry rows
 * ```
 *
 * A level row's numbers are indicators on the group's **index bars**, computed
 * on the server; the browser keeps the drill. The members are the base
 * screen's rows *after its filter* and *before the ticks* — a tick narrows what
 * is shown below and never what a level is made of.
 */

import type {
  ScreenerColumn,
  ScreenerFilterNode,
  ScreenerTopDown,
  ScreenerTopDownTicks,
} from "@qufolio/model";
import type { ScreenerFilter } from "@qufolio/screener-grid";

export type LevelKey = "sector" | "industry";
export const LEVEL_KEYS: readonly LevelKey[] = ["sector", "industry"];

/** One base row, as much of it as TopDown reads. */
export interface MemberRow {
  symbol: string;
  sector?: string | null;
  industry?: string | null;
  /** The base row's own numbers that a level adds up (`MEMBER_FIELDS`). */
  values?: Record<string, number | null>;
}

// ── level columns built from the members, as QuantVitals does ────────────────

/** What a member row carries for `memberAggregates`. */
export const MEMBER_FIELDS = ["changeCc", "close", "fMom2", "fMom5", "fMom13"] as const;

/**
 * Level columns that are **the members' numbers added up**, exactly as
 * QuantVitals' `calc_jas_topdown_over` (owner, 2026-10-16: "same as QV"):
 *
 *   Δ   = Σ changeCc
 *   %   = Σ changeCc / Σ previous close × 100
 *   fMn = Σ fMomN / members          (a null counts as 0, as QV's fill_null)
 *
 * Every other level column is evaluated on the group's index bars (RSI).
 */
export const MEMBER_AGGREGATED: ReadonlySet<string> = new Set(["changeCc", "changePercentCc", "fMom2", "fMom5", "fMom13"]);

/** One level's member-built values, per group name. */
export function memberAggregates(
  members: readonly MemberRow[],
  key: LevelKey,
): Map<string, Record<string, number | null>> {
  const groups = new Map<string, { n: number; chg: number; prev: number; fm: Record<string, number> }>();
  for (const m of members) {
    const name = m[key];
    if (!name) continue; // QV drops a blank sector
    const v = m.values ?? {};
    const num = (x: number | null | undefined) => (typeof x === "number" && Number.isFinite(x) ? x : 0);
    const chg = num(v.changeCc);
    const prev = num(v.close) - chg;
    // QV drops a row whose own % is infinite: a zero previous close.
    if (chg !== 0 && prev === 0) continue;
    const g = groups.get(name) ?? { n: 0, chg: 0, prev: 0, fm: { fMom2: 0, fMom5: 0, fMom13: 0 } };
    g.n += 1;
    g.chg += chg;
    g.prev += prev;
    for (const f of ["fMom2", "fMom5", "fMom13"]) g.fm[f]! += num(v[f]);
    groups.set(name, g);
  }
  const out = new Map<string, Record<string, number | null>>();
  for (const [name, g] of groups) {
    out.set(name, {
      changeCc: g.chg,
      changePercentCc: g.prev !== 0 ? (g.chg / g.prev) * 100 : null,
      fMom2: g.fm.fMom2! / g.n,
      fMom5: g.fm.fMom5! / g.n,
      fMom13: g.fm.fMom13! / g.n,
    });
  }
  return out;
}

/** The server's rows with the member-built columns laid over them. */
export function withMemberValues(rows: readonly LevelRow[], members: readonly MemberRow[], key: LevelKey): LevelRow[] {
  const agg = memberAggregates(members, key);
  return rows.map((r) => ({ ...r, values: { ...r.values, ...(agg.get(r.key) ?? {}) } }));
}

/** One level row from the server. */
export interface LevelRow {
  key: string;
  members: number;
  values: Record<string, number | null>;
}

// ── ticks ────────────────────────────────────────────────────────────────────

export function ticksOf(td: ScreenerTopDown | undefined): { sector: string[]; industry: string[] } {
  return { sector: td?.ticks?.sector ?? [], industry: td?.ticks?.industry ?? [] };
}

/** Ticks in contract shape: empty lists are absent, nothing ticked is undefined. */
export function packTicks(sector: readonly string[], industry: readonly string[]): ScreenerTopDownTicks | undefined {
  const out: ScreenerTopDownTicks = {};
  if (sector.length) out.sector = [...sector];
  if (industry.length) out.industry = [...industry];
  return out.sector || out.industry ? out : undefined;
}

/**
 * Tick or untick one row. **Unticking a sector drops its industries' ticks**
 * (owner, 2026-10-16) — kept, they would narrow the Symbols grid invisibly.
 * Ticking a sector drops industry ticks that fall outside the ticked sectors
 * for the same reason.
 */
export function toggleTick(
  ticks: { sector: string[]; industry: string[] },
  level: LevelKey,
  name: string,
  rows: readonly MemberRow[],
): { sector: string[]; industry: string[] } {
  const set = new Set(ticks[level]);
  const unticked = set.has(name);
  if (unticked) set.delete(name);
  else set.add(name);
  const next = { ...ticks, [level]: [...set] };
  if (level !== "sector") return next;
  // The sector's own industries go with it — even when it was the last sector
  // ticked, where "inside the ticked sectors" would otherwise keep them all.
  const own = new Set(rows.filter((r) => r.sector === name).map((r) => r.industry));
  const industry = unticked ? next.industry.filter((i) => !own.has(i)) : next.industry;
  return { ...next, industry: industriesWithin(industry, next.sector, rows) };
}

/** Every row of one level ticked, or none — the header checkbox. */
export function setAllTicks(
  ticks: { sector: string[]; industry: string[] },
  level: LevelKey,
  names: readonly string[],
  on: boolean,
  rows: readonly MemberRow[],
): { sector: string[]; industry: string[] } {
  const next = { ...ticks, [level]: on ? [...names] : [] };
  if (level !== "sector") return next;
  // Every sector unticked takes every industry tick with it.
  return { ...next, industry: on ? industriesWithin(next.industry, next.sector, rows) : [] };
}

/** `industry` ticks that lie inside the ticked sectors (all, when none is). */
export function industriesWithin(
  industry: readonly string[],
  sector: readonly string[],
  rows: readonly MemberRow[],
): string[] {
  if (!sector.length) return [...industry];
  const allowed = new Set(rows.filter((r) => r.sector && sector.includes(r.sector)).map((r) => r.industry));
  return industry.filter((i) => allowed.has(i));
}

/** The industries the Industry grid lists: those inside the ticked sectors. */
export function visibleIndustries(sector: readonly string[], rows: readonly MemberRow[]): Set<string> | undefined {
  if (!sector.length) return undefined;
  return new Set(
    rows.filter((r) => r.sector && sector.includes(r.sector) && r.industry).map((r) => r.industry as string),
  );
}

/** The rows the Symbols grid shows: the members narrowed by the ticks. */
export function narrowed(rows: readonly MemberRow[], ticks: { sector: string[]; industry: string[] }): MemberRow[] {
  return rows.filter(
    (r) =>
      (!ticks.sector.length || (r.sector != null && ticks.sector.includes(r.sector))) &&
      (!ticks.industry.length || (r.industry != null && ticks.industry.includes(r.industry))),
  );
}

/**
 * `filter` with the ticks ANDed in, on the way to the Symbols grid only —
 * `withRecsDirection`'s shape. Applied even with the panel's filters off: the
 * ticks are which drill you are reading, not a condition.
 */
export function withTicks(
  filter: ScreenerFilter | undefined,
  ticks: { sector: string[]; industry: string[] },
): ScreenerFilter | undefined {
  const parts: ScreenerFilterNode[] = [];
  if (ticks.sector.length) parts.push({ field: "sector", op: "in", values: [...ticks.sector] } as ScreenerFilterNode);
  if (ticks.industry.length) parts.push({ field: "industry", op: "in", values: [...ticks.industry] } as ScreenerFilterNode);
  if (!parts.length) return filter;
  const children = filter ? [filter as ScreenerFilterNode, ...parts] : parts;
  return children.length === 1 ? children[0] : ({ op: "all", children } as ScreenerFilterNode);
}

// ── the chart ────────────────────────────────────────────────────────────────

/**
 * Which members the chart's index is over, or undefined for no index.
 *
 * Industry ticks win over sector ticks (owner, 2026-10-16); several ticked is
 * one index over all of them; nothing ticked is no index.
 */
export function indexMembers(rows: readonly MemberRow[], ticks: { sector: string[]; industry: string[] }): string[] | undefined {
  const level: LevelKey | undefined = ticks.industry.length ? "industry" : ticks.sector.length ? "sector" : undefined;
  if (!level) return undefined;
  const picked = new Set(ticks[level]);
  const members = rows.filter((r) => r[level] != null && picked.has(r[level] as string)).map((r) => r.symbol);
  return members.length ? members : undefined;
}

/** What the index's legend says: `Technology + Energy`. */
export function indexLabel(ticks: { sector: string[]; industry: string[] }): string {
  const names = ticks.industry.length ? ticks.industry : ticks.sector;
  return names.length > 2 ? `${names.slice(0, 2).join(" + ")} + ${names.length - 2} more` : names.join(" + ");
}

/** Hex digits of the hash kept — `~TDe.` + 15 is 20 characters, the server's limit. */
const HASH_DIGITS = 15;

/**
 * FNV-1a 64 of the sorted, de-duplicated, upper-cased members — the same
 * function as `qf/topdown.members_hash`; both tests pin one value.
 */
export function membersHash(symbols: Iterable<string>): string {
  const text = [...new Set([...symbols].map((s) => s.trim().toUpperCase()).filter(Boolean))].sort().join(",");
  let value = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (const byte of new TextEncoder().encode(text)) {
    value ^= BigInt(byte);
    value = (value * prime) & mask;
  }
  return value.toString(16).padStart(16, "0").slice(0, HASH_DIGITS);
}

export const INDEX_PREFIX = "~TD";

/** `~TDe.<hash>` — never a ticker (`~` is in none) and never an expression. */
export function indexSymbol(symbols: Iterable<string>, weighting: ScreenerTopDown["weighting"] = "equal"): string {
  return `${INDEX_PREFIX}${weighting === "cap" ? "c" : "e"}.${membersHash(symbols)}`;
}

export function isIndexSymbol(symbol: string | undefined): boolean {
  return typeof symbol === "string" && symbol.startsWith(INDEX_PREFIX);
}

// ── the registry the chart request reads ─────────────────────────────────────

/**
 * Members per index symbol, for `chart-feed.ts` to send beside the symbol.
 *
 * Module scope, `symbol-index.ts`'s shape. **Kept in localStorage** so a page
 * load charts the index the workspace saved before the TopDown panel has had
 * its rows; per browser, wrapped, and a miss only means the chart says
 * "symbol not found" until the panel names the index again.
 */
const STORE_KEY = "qf.topdown.indexes";
const MAX_HELD = 32;
const held = new Map<string, { members: string[]; label: string }>();
let loaded = false;

function load(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = globalThis.localStorage?.getItem(STORE_KEY);
    const parsed = raw ? (JSON.parse(raw) as [string, { members: string[]; label: string }][]) : [];
    for (const [symbol, entry] of parsed) {
      if (isIndexSymbol(symbol) && Array.isArray(entry?.members)) held.set(symbol, entry);
    }
  } catch {
    // Private window, blocked storage: the registry is in memory only.
  }
}

function save(): void {
  try {
    globalThis.localStorage?.setItem(STORE_KEY, JSON.stringify([...held].slice(-MAX_HELD)));
  } catch {
    // As above.
  }
}

/** Name an index: returns its symbol, and holds its members for the request. */
export function registerIndex(members: readonly string[], label: string, weighting: ScreenerTopDown["weighting"] = "equal"): string {
  load();
  const symbol = indexSymbol(members, weighting);
  held.delete(symbol);
  held.set(symbol, { members: [...new Set(members.map((m) => m.toUpperCase()))].sort(), label });
  while (held.size > MAX_HELD) held.delete(held.keys().next().value as string);
  save();
  return symbol;
}

/**
 * The screener bar's Σ: one index over the rows a screen shows — `Impulse (501)`.
 *
 * Index symbols among the rows are left out (an index is not a member of
 * another); fewer than two members is no index. The members are those of the
 * moment of the click: the index does not follow the filter afterwards.
 */
export const SCREEN_INDEX_MIN = 2;

export function registerScreenIndex(
  symbols: Iterable<string>,
  name: string,
  weighting: ScreenerTopDown["weighting"] = "equal",
): string | undefined {
  const members = [...new Set([...symbols].map((s) => s.trim().toUpperCase()).filter((s) => s && !isIndexSymbol(s)))];
  if (members.length < SCREEN_INDEX_MIN) return undefined;
  const label = `${name.trim() || "Screen"} (${members.length})${weighting === "cap" ? " · Cap" : ""}`;
  return registerIndex(members, label, weighting);
}

/**
 * Σ's weighting: one choice for every screen, kept in this browser (owner,
 * 2026-10-21). Storage that throws or holds anything else reads as equal.
 */
const SCREEN_WEIGHTING_KEY = "qf.screenIndex.weighting";

export function screenIndexWeighting(): NonNullable<ScreenerTopDown["weighting"]> {
  try {
    return globalThis.localStorage?.getItem(SCREEN_WEIGHTING_KEY) === "cap" ? "cap" : "equal";
  } catch {
    return "equal";
  }
}

export function setScreenIndexWeighting(weighting: NonNullable<ScreenerTopDown["weighting"]>): void {
  try {
    globalThis.localStorage?.setItem(SCREEN_WEIGHTING_KEY, weighting);
  } catch {
    // Private window, blocked storage: the choice lasts this page only.
  }
}

export function membersForIndex(symbol: string): string[] | undefined {
  if (!isIndexSymbol(symbol)) return undefined;
  load();
  return held.get(symbol)?.members;
}

/**
 * What a chart calls its symbol: an index by what it holds —
 * `Consumer Defensive + Consumer Cyclical · Index` — never by its `~TDe.<hash>`,
 * which is an id for the server and reads as nonsense. Anything else unchanged.
 * An index this browser has no label for (another browser made it) says so
 * plainly rather than printing the hash.
 */
export function symbolTitle(symbol: string): string {
  if (!isIndexSymbol(symbol)) return symbol;
  const label = labelForIndex(symbol);
  return label ? `${label} · Index` : "Sector index";
}

export function labelForIndex(symbol: string): string | undefined {
  if (!isIndexSymbol(symbol)) return undefined;
  load();
  return held.get(symbol)?.label;
}

/** Tests only. */
export function _resetIndexes(): void {
  held.clear();
  loaded = true;
}

// ── layout ───────────────────────────────────────────────────────────────────

/** Below this panel width one row does not fit, and auto falls back to tall. */
export const WIDE_MIN_WIDTH = 1200;

/**
 * `tall` (levels on top) or `wide` (one row). Auto follows the dock: left or
 * right is tall, top or bottom is wide — then narrower than
 * {@link WIDE_MIN_WIDTH} is tall whatever the dock says.
 */
export function layoutFor(
  orientation: ScreenerTopDown["orientation"],
  dock: "left" | "right" | "top" | "bottom" | undefined,
  width: number,
): "tall" | "wide" {
  if (orientation === "tall" || orientation === "wide") return orientation;
  const byDock = dock === "top" || dock === "bottom" ? "wide" : "tall";
  return byDock === "wide" && width > 0 && width < WIDE_MIN_WIDTH ? "tall" : byDock;
}

// ── defaults ─────────────────────────────────────────────────────────────────

/**
 * A new TopDown's level columns, QuantVitals' set (owner, 2026-10-16): Δ, %,
 * fM2, fM5, fM13 built from the members (`MEMBER_AGGREGATED`), then RSI 14 on
 * the index bars — listed but unticked.
 */
export function defaultLevelColumns(): ScreenerColumn[] {
  return [
    { field: "changeCc", header: "Δ", type: "number", precision: 2 },
    { field: "changePercentCc", header: "%", type: "number", precision: 2 },
    { field: "fMom2", header: "fM2", type: "number", precision: 2 },
    { field: "fMom5", header: "fM5", type: "number", precision: 2 },
    { field: "fMom13", header: "fM13", type: "number", precision: 2 },
    {
      field: "tdRsi14",
      header: "RSI",
      type: "number",
      precision: 1,
      hide: true,
      computed: {
        expression: {
          kind: "call",
          fn: "rsi",
          args: [
            { kind: "field", field: "close" },
            { kind: "literal", value: 14 },
          ],
        },
        source: "rsi(c, 14)",
      },
    },
  ];
}

/**
 * A TopDown still holding the first default (Δ, %, RSI shown) — every one made
 * before fM was added (2026-10-16) — gets today's set; anything somebody
 * edited is left alone. `null` when there is nothing to change.
 */
export function upgradedLevels(td: ScreenerTopDown): ScreenerTopDown | null {
  const old = (cols: readonly ScreenerColumn[] | undefined) =>
    (cols ?? []).map((c) => `${c.field}${c.hide ? "-" : ""}`).join(",") === "changeCc,changePercentCc,tdRsi14";
  if (!td.levels.length || !td.levels.every((l) => old(l.columns))) return null;
  return { ...td, levels: td.levels.map((l) => ({ ...l, columns: defaultLevelColumns() })) };
}

/** A new TopDown over `base`. */
export function newTopDown(base: string): ScreenerTopDown {
  return {
    base,
    orientation: "auto",
    weighting: "equal",
    levels: LEVEL_KEYS.map((key) => ({ key, columns: defaultLevelColumns() })),
  };
}

/** A level's columns, as the server is asked for them. */
export function levelsAsk(td: ScreenerTopDown): { key: LevelKey; columns: ScreenerColumn[] }[] {
  // The member-built columns are this browser's sum, not the server's index.
  return td.levels.map((l) => ({
    key: l.key,
    columns: (l.columns ?? []).filter((c) => !c.hide && !MEMBER_AGGREGATED.has(c.field)),
  }));
}

/** A value signature for when to ask again: the members, and the columns. */
export function levelsKey(members: readonly MemberRow[], td: ScreenerTopDown): string {
  // A plain string, not a hash: compared once per push, and BigInt FNV over
  // ten thousand rows would cost more than the comparison saves.
  const who = members
    .map((m) => `${m.symbol}|${m.sector ?? ""}|${m.industry ?? ""}`)
    .sort()
    .join(",");
  // The weighting too: switching Equal ↔ Cap must ask again.
  return `${who}#${td.weighting ?? "equal"}#${JSON.stringify(levelsAsk(td))}`;
}
